import { useEffect } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import { useTabsUIStore, TABS_VIEW_MODE_KEY } from '@/stores/tabsUIStore';
import type { TabViewMode } from '@/stores/tabsUI/tabsUI.types';

/**
 * Single source of truth for tabs list/tables view mode.
 * When HIDE_TABLE_VIEW is enabled, forces the store to `list`
 */
export function useTabsViewMode() {
  const viewMode = useTabsUIStore((s) => s.viewMode);
  const setViewMode = useTabsUIStore((s) => s.setViewMode);
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlag();
  const hideTableView = isEnabled(FEATURE_FLAGS.HIDE_TABLE_VIEW);

  useEffect(() => {
    if (flagsLoading) return;
    if (hideTableView && viewMode === 'tables') {
      setViewMode('list');
    }
  }, [flagsLoading, hideTableView, viewMode, setViewMode]);

  useEffect(() => {
    if (flagsLoading && viewMode === 'tables') return;
    // Wait for the store sync above before persisting.
    if (hideTableView && viewMode === 'tables') return;
    localStorage.setItem(TABS_VIEW_MODE_KEY, viewMode);
  }, [viewMode, flagsLoading, hideTableView]);

  const resolvedViewMode: TabViewMode =
    viewMode === 'tables' && (flagsLoading || hideTableView) ? 'list' : viewMode;

  return {
    viewMode: resolvedViewMode,
    setViewMode,
    hideTableView,
    flagsLoading,
    showTablesToggle: !flagsLoading && !hideTableView,
  };
}
